import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Check } from "@phosphor-icons/react";
import { api, ensureUserId, setName as saveName } from "@/lib/api";

const STEPS = [
  {
    key: "name",
    title: "What should we call you?",
    sub: "Your mentors will greet you by name.",
    type: "text",
  },
  {
    key: "goals",
    title: "What's your big move this year?",
    sub: "Pick up to 3.",
    type: "multi",
    options: [
      "Become a founder",
      "Get financially literate",
      "Build a startup",
      "Transition careers",
      "Become a creator",
      "Master product strategy",
      "Sharpen storytelling",
      "Apply AI in my work",
    ],
  },
  {
    key: "interests",
    title: "Where do you want to apply AI?",
    sub: "Pick the industry or field — we'll match mentors who teach AI for it.",
    type: "multi",
    options: [
      "Finance",
      "Healthcare",
      "E-commerce & Retail",
      "Marketing",
      "Education",
      "Manufacturing",
      "Legal",
      "Real Estate",
      "HR & Recruitment",
      "Creative & Media",
      "Engineering",
      "Sales",
      "Operations",
      "Hospitality",
    ],
  },
  {
    key: "skill_level",
    title: "Where are you on the climb?",
    type: "single",
    options: ["Beginner", "Intermediate", "Advanced"],
  },
  {
    key: "learning_style",
    title: "How do you learn best?",
    type: "single",
    options: ["Conversational", "Hands-on projects", "Bite-sized lessons", "Deep dives"],
  },
  {
    key: "time_per_day",
    title: "Daily time commitment?",
    type: "single",
    options: ["10m", "30m", "60m", "90m+"],
  },
];

export default function Onboarding() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [data, setData] = useState({ name: "", goals: [], interests: [], skill_level: "Beginner", learning_style: "Conversational", time_per_day: "30m" });
  const [submitting, setSubmitting] = useState(false);

  const s = STEPS[step];
  const progress = ((step + 1) / STEPS.length) * 100;

  const setVal = (val) => setData((d) => ({ ...d, [s.key]: val }));
  const toggle = (val) =>
    setData((d) => {
      const arr = d[s.key] || [];
      const has = arr.includes(val);
      const next = has ? arr.filter((x) => x !== val) : arr.length < 3 ? [...arr, val] : arr;
      return { ...d, [s.key]: next };
    });

  const canNext = () => {
    const v = data[s.key];
    if (s.type === "multi") return v && v.length > 0;
    if (s.type === "text") return (v || "").trim().length >= 1;
    return !!v;
  };

  const next = async () => {
    if (step < STEPS.length - 1) return setStep(step + 1);
    setSubmitting(true);
    const uid = ensureUserId();
    saveName(data.name);
    try {
      await api.post("/onboarding", { user_id: uid, ...data });
    } catch (e) { /* ignore */ }
    localStorage.setItem("lumen_onboarded", "1");
    setSubmitting(false);
    nav("/home");
  };

  return (
    <div className="mesh-bg min-h-screen flex flex-col px-6 pt-12 pb-10" data-testid="onboarding-screen">
      <div className="flex items-center gap-3">
        <button
          onClick={() => (step === 0 ? null : setStep(step - 1))}
          data-testid="onb-back-btn"
          disabled={step === 0}
          aria-label="Back"
          className="w-9 h-9 rounded-full glass flex items-center justify-center active:scale-95 transition disabled:opacity-0 disabled:pointer-events-none"
        >
          <ArrowLeft size={16} />
        </button>
        <div className="flex-1 h-1 rounded-full bg-white/5 overflow-hidden">
          <motion.div
            className="h-full bg-white rounded-full"
            initial={false}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          />
        </div>
        <span className="text-[10px] uppercase tracking-widest text-zinc-500">{step + 1}/{STEPS.length}</span>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={s.key}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -16 }}
          transition={{ duration: 0.35 }}
          className="flex-1 flex flex-col mt-12"
        >
          <h1 className="font-display text-4xl sm:text-5xl font-light leading-tight tracking-tight">{s.title}</h1>
          {s.sub && <p className="mt-3 text-zinc-400 text-sm">{s.sub}</p>}

          <div className="mt-10 flex-1">
            {s.type === "text" && (
              <input
                data-testid="onb-name-input"
                autoFocus
                value={data.name}
                onChange={(e) => setVal(e.target.value)}
                placeholder="Your name"
                className="w-full bg-transparent border-b border-white/15 focus:border-white/50 outline-none text-2xl font-display py-3 placeholder:text-zinc-700 transition-colors"
              />
            )}
            {(s.type === "single" || s.type === "multi") && (
              <div className="flex flex-wrap gap-2">
                {s.options.map((opt) => {
                  const selected =
                    s.type === "multi" ? (data[s.key] || []).includes(opt) : data[s.key] === opt;
                  return (
                    <button
                      key={opt}
                      data-testid={`onb-opt-${opt}`}
                      onClick={() => (s.type === "multi" ? toggle(opt) : setVal(opt))}
                      className={`px-4 py-2.5 rounded-full text-sm border transition-all active:scale-95 ${
                        selected
                          ? "bg-white text-black border-white"
                          : "bg-white/[0.03] text-zinc-200 border-white/10 hover:bg-white/[0.07]"
                      }`}
                    >
                      {selected && <Check size={14} weight="bold" className="inline -mt-0.5 mr-1" />}
                      {opt}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </motion.div>
      </AnimatePresence>

      <button
        data-testid="onb-next-btn"
        disabled={!canNext() || submitting}
        onClick={next}
        className="mt-8 w-full rounded-full bg-white text-black font-medium py-4 flex items-center justify-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed active:scale-[0.98] transition-transform"
      >
        {step === STEPS.length - 1 ? "Enter SuperLearn" : "Continue"}
        <ArrowRight size={18} weight="bold" />
      </button>
    </div>
  );
}
